/**
 * B-4v2 — Today health-sync home data hook.
 *
 * Backs the native Today strip that makes the (previously invisible) H①
 * sleep/calories background sync EVIDENT + manually refreshable. The server is
 * the authority: `GET /api/health-metrics/status` is SELF-ONLY and returns
 * freshness metadata only (last captured_at + per-family last data day +
 * whether H① is active) — never a raw health value.
 *
 * FAILS CLOSED, like useCoachReactionToday:
 *   - the client flag `health_home_v1` is a surface-readiness signal — when off
 *     (or non-native) we never request and hold `null` (the slot renders the
 *     legacy ReadinessSlot / nothing);
 *   - any fetch/parse error → treat as "never synced" (the connect state), the
 *     safe default (no shame, no fabricated freshness).
 *
 * `refresh()` reuses the EXISTING native sync path (`syncHealthMetrics`, force)
 * — it does NOT reimplement any HealthKit call. `syncHealthMetrics` triggers
 * the H① permission request on first run, so the same affordance serves both
 * the never-synced "Connect Apple Health" CTA and the synced manual refresh.
 * After a sync attempt we re-fetch the (server-authoritative) status.
 *
 * W1-TODAY-FIX2 — ONE READ PER MOUNT WAVE. Several components on Today read this status
 * (the hero's strip, the 2.0 page's provider), and each used to open its own
 * `GET /api/health-metrics/status`; with `today_2_0_v1` on and no session to show, the
 * provider and the legacy hero's strip each asked once, where the legacy page asks once.
 * The first reader of a signed-in user's wave now starts the request and every reader that
 * mounts while it is in flight, or within SHARE_MAX_AGE_MS of its answer, gets the same
 * answer instead of asking again. The wave ends when the last reader unmounts, so leaving
 * Today and coming back always reads fresh, and a manual `refresh()` always reads fresh
 * (its answer is then what a later reader in the window gets). With no client or user to
 * key on (a signed-out render, a test that supplies neither) nothing is shared and each
 * reader asks for itself, exactly as before.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthFetch, type AuthFetchFn } from './useAuthFetch';
import { useServerFlag } from './useServerFlag';
import { useAuthContext } from '../contexts/AuthContext';
import { isNativeApp } from '../lib/platform';
import { syncHealthMetrics } from '../lib/health/healthKit';

export interface HealthSyncStatus {
  /** H① background sync is running (server `health_context_v1`). */
  active: boolean;
  /** ISO timestamp of the most recent sync upload, or null if never synced. */
  lastCapturedAt: string | null;
  /** Most recent LOCAL data day across all families ("YYYY-MM-DD"), or null. */
  lastSyncedDay: string | null;
  /** Per-metric-family most recent data day; a family is null until it has data. */
  families: {
    sleep?: string | null;
    activeEnergy?: string | null;
    restingHr?: string | null;
    hrv?: string | null;
  };
}

export interface UseHealthSyncStatus {
  /** Server status, or null while the surface is inactive / not yet loaded. */
  status: HealthSyncStatus | null;
  /** True once the first status fetch has resolved (or been skipped). */
  loaded: boolean;
  /** A manual sync is in flight (drives the refresh affordance's busy state). */
  refreshing: boolean;
  /** Trigger the native H① pull, then re-read status. No-op off-native/flag. */
  refresh: () => void;
}

const neverSynced = (): HealthSyncStatus => ({
  active: false,
  lastCapturedAt: null,
  lastSyncedDay: null,
  families: {},
});

/** The status read itself. Never rejects: any failure fails closed to "never synced". */
async function readStatus(fetchAuthed: AuthFetchFn): Promise<HealthSyncStatus> {
  try {
    const res = await fetchAuthed('/api/health-metrics/status');
    const body = res.ok ? ((await res.json()) as HealthSyncStatus) : null;
    return body ?? neverSynced();
  } catch {
    // Fail closed to "never synced" (the connect state) — the safe default.
    return neverSynced();
  }
}

/** How long a settled answer is still handed to a reader that mounts after it (W1-TODAY-FIX2). */
const SHARE_MAX_AGE_MS = 10_000;

interface SharedRead {
  /** The wave's read, in flight or settled; null until the first reader asks. */
  promise: Promise<HealthSyncStatus> | null;
  /** When it settled, or null while in flight. */
  settledAt: number | null;
  /** Readers mounted on this client and user; the wave ends at zero. */
  readers: number;
}
const sharedReads = new WeakMap<object, Map<string, SharedRead>>();

/** Join the signed-in user's wave for the length of a reader's life. Returns the leave function. */
function joinWave(client: object | null, uid: string | null): () => void {
  if (!client || !uid) return () => {};
  let perUser = sharedReads.get(client);
  if (!perUser) {
    perUser = new Map();
    sharedReads.set(client, perUser);
  }
  let entry = perUser.get(uid);
  if (!entry) {
    entry = { promise: null, settledAt: null, readers: 0 };
    perUser.set(uid, entry);
  }
  entry.readers += 1;
  const mine = entry;
  const users = perUser;
  let left = false;
  return () => {
    if (left) return;
    left = true;
    mine.readers -= 1;
    if (mine.readers <= 0 && users.get(uid) === mine) users.delete(uid);
  };
}

/**
 * The wave's status read: start it, or hand back the one already in flight (or settled
 * moments ago). `fresh` always starts a new one, and its answer becomes the wave's.
 * Outside a wave (no client or user to key on) it is just a read.
 */
function waveRead(
  client: object | null,
  uid: string | null,
  fetchAuthed: AuthFetchFn,
  fresh: boolean,
): Promise<HealthSyncStatus> {
  const entry = client && uid ? sharedReads.get(client)?.get(uid) : undefined;
  if (!entry) return readStatus(fetchAuthed);
  const reusable =
    !fresh &&
    entry.promise !== null &&
    (entry.settledAt === null || Date.now() - entry.settledAt < SHARE_MAX_AGE_MS);
  if (reusable && entry.promise) return entry.promise;
  entry.settledAt = null;
  const started: Promise<HealthSyncStatus> = readStatus(fetchAuthed).then((s) => {
    if (entry.promise === started) entry.settledAt = Date.now();
    return s;
  });
  entry.promise = started;
  return started;
}

export function useHealthSyncStatus(): UseHealthSyncStatus {
  const homeOn = useServerFlag('health_home_v1');
  const fetchAuthed = useAuthFetch();
  const { supabase, user } = useAuthContext();
  const client: object | null = supabase ?? null;
  const uid: string | null = user?.id ?? null;
  // The latest client, read at call time: `load` is re-created only when the user, the
  // flag or the authed fetch change (the fetch is re-created with the client), exactly as
  // before, so a caller that hands back a fresh client object each render cannot make the
  // read re-run on every render.
  const clientRef = useRef<object | null>(client);
  clientRef.current = client;
  const native = isNativeApp();
  const enabled = homeOn && native;

  const [status, setStatus] = useState<HealthSyncStatus | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const load = useCallback(
    async (alive: () => boolean, fresh = false) => {
      if (!enabled) {
        if (alive()) {
          setStatus(null);
          setLoaded(true);
        }
        return;
      }
      const body = await waveRead(clientRef.current, uid, fetchAuthed, fresh);
      if (alive()) {
        setStatus(body);
        setLoaded(true);
      }
    },
    [enabled, fetchAuthed, uid],
  );

  // Declared before the load effect: a reader is in the wave before it reads.
  useEffect(() => {
    if (!enabled) return;
    return joinWave(client, uid);
  }, [enabled, client, uid]);

  useEffect(() => {
    let live = true;
    void load(() => live);
    return () => {
      live = false;
    };
  }, [load]);

  const refresh = useCallback(() => {
    if (!enabled || refreshing) return;
    setRefreshing(true);
    let live = true;
    void (async () => {
      try {
        // Reuse the existing native sync (force = skip throttle). This is the
        // ONLY health call — it requests HealthKit permission on first run and
        // uploads via the already-gated POST /api/health-metrics/daily.
        await syncHealthMetrics(fetchAuthed, true);
      } finally {
        // Always a fresh read: it has to see the sync that just ran (W1-TODAY-FIX2).
        await load(() => live, true);
        if (live) setRefreshing(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [enabled, refreshing, fetchAuthed, load]);

  return { status, loaded, refreshing, refresh };
}
